import { authStore } from "~/providers/store/auth/store";
import {
  createFakeScaleWrapper,
  type FakeScaleOptions,
  type FakeScaleWrapper
} from "~/services/fake-scale-wrapper";

/* eslint-disable no-console -- Development initialization */
type ScannerType = "barcode" | "qr" | "qr-child-id";
type SimulateScannerArgs = {
  value: string;
  type: ScannerType;
};

/**
 * Web Till API for testing functions without physical hardware
 * This is only available in development mode
 */
class WebTillAPI {
  private listeners = new Map<string, Set<(data: unknown) => void>>();
  private fakeScale: FakeScaleWrapper | null = null;

  constructor() {
    if (!authStore.getState().isKanplaAdmin) {
      return;
    }

    // Expose the API globally for easy access, ensuring window.Kanpla exists
    if (typeof window.Kanpla === "undefined") {
      window.Kanpla = {};
    }

    window.Kanpla.tillAPI = this;

    console.log(
      "Kanpla Web Till API initialized - available at window.Kanpla.tillAPI"
    );
  }

  /**
   * Simulate card reader input
   * @param value - The value to simulate
   */
  simulateCardReader(value: string) {
    const code = value;
    console.log("Simulating card reader input", { value, code });

    // Type each character as a keypress
    for (const char of code) {
      document.dispatchEvent(
        new KeyboardEvent("keydown", {
          key: char,
          code: `Key${char.toUpperCase()}`,
          charCode: char.charCodeAt(0),
          keyCode: char.charCodeAt(0),
          bubbles: true,
          cancelable: true
        })
      );

      document.dispatchEvent(
        new KeyboardEvent("keypress", {
          key: char,
          code: `Key${char.toUpperCase()}`,
          charCode: char.charCodeAt(0),
          keyCode: char.charCodeAt(0),
          bubbles: true,
          cancelable: true
        })
      );

      document.dispatchEvent(
        new KeyboardEvent("keyup", {
          key: char,
          code: `Key${char.toUpperCase()}`,
          keyCode: char.charCodeAt(0),
          bubbles: true,
          cancelable: true
        })
      );
    }

    // Press Enter
    document.dispatchEvent(
      new KeyboardEvent("keydown", {
        key: "Enter",
        code: "Enter",
        keyCode: 13,
        bubbles: true,
        cancelable: true
      })
    );

    this.emit("cardReader", { value });
  }

  /**
   * Simulate barcode scanner input
   * @param barcode - The barcode to simulate
   */
  simulateScanner({ value, type }: SimulateScannerArgs) {
    let code = value;
    if (type === "qr-child-id") {
      code = `START${value}END`;
    }

    console.log("Simulating scanner input", { value, type, code });

    // Type each character as a keypress
    for (const char of code) {
      document.dispatchEvent(
        new KeyboardEvent("keydown", {
          key: char,
          code: `Key${char.toUpperCase()}`,
          charCode: char.charCodeAt(0),
          keyCode: char.charCodeAt(0),
          bubbles: true,
          cancelable: true
        })
      );
    }

    // Press Shift after a short delay to simulate QR scanner behavior
    setTimeout(() => {
      document.dispatchEvent(
        new KeyboardEvent("keydown", {
          key: "Shift",
          code: "ShiftLeft",
          keyCode: 16,
          bubbles: true,
          cancelable: true
        })
      );
    }, 100);

    this.emit("scanner", { value, type });
  }

  /**
   * Stand in for the wrapper and a CAS scale so the pairing flow runs without hardware.
   * @param options - the simulated scale's model and the unit it is calibrated to
   */
  attachScale(options: FakeScaleOptions) {
    this.fakeScale?.detach();
    this.fakeScale = createFakeScaleWrapper(options);
    this.fakeScale.attach();
    console.log("Fake scale attached", options);
    return this.fakeScale;
  }

  /**
   * Put an item on the fake scale
   * @param displayDigits - what the scale's own display shows, without the decimal point, e.g. "01150"
   */
  placeOnScale(displayDigits: string) {
    if (!this.fakeScale) {
      console.warn("No fake scale attached; call attachScale first");
      return;
    }
    this.fakeScale.placeItem(displayDigits);
  }

  clearScale() {
    this.fakeScale?.removeItem();
  }

  detachScale() {
    this.fakeScale?.detach();
    this.fakeScale = null;
  }

  /**
   * Add event listener
   */
  on(event: string, callback: (data: unknown) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.add(callback);
    }
  }

  /**
   * Remove event listener
   */
  off(event: string, callback: (data: unknown) => void) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback);
    }
  }

  /**
   * Emit event to listeners
   */
  private emit(event: string, data: unknown) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(error as Error, "Error in Web Till API event listener");
        }
      });
    }
  }

  /**
   * Show help information in console
   */
  help() {
    console.log(
      `
🚀 Web Till API Help

Available methods:
- window.Kanpla.tillAPI.simulateCardReader(value)     - Simulate card reader input
- window.Kanpla.tillAPI.simulateScanner({ value, type }) - Simulate scanner input
- window.Kanpla.tillAPI.help()                          - Show this help
- window.Kanpla.tillAPI.attachScale({ model, unit })   - Fake the wrapper and a scale (model "pdn" | "pd2", unit "g" | "kg" | "lb" | "oz")
- window.Kanpla.tillAPI.placeOnScale("01150")          - Put an item on it; digits as the scale's display shows them, no point
- window.Kanpla.tillAPI.clearScale()                    - Take the item off
- window.Kanpla.tillAPI.detachScale()                   - Remove the fake scale

Examples:
- window.Kanpla.tillAPI.simulateCardReader("<card-hex-code>")
- window.Kanpla.tillAPI.simulateScanner({ value: "<barcode-value>", type: "barcode" })
- window.Kanpla.tillAPI.simulateScanner({ value: "<qr-value>", type: "qr" })
- window.Kanpla.tillAPI.simulateScanner({ value: "<qr-child-id>", type: "qr-child-id" })
`
    );
  }
}

// Create and export the singleton instance
export const webTillAPI = new WebTillAPI();
